import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import LEGAL_TEMPLATES from "./legalTemplates";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Chunked upload settings: 4 MB keeps us well below Cloudflare's request body limits
// on every plan (Free: 100 MB, but some proxies/gateways trim down to ~25 MB).
const CHUNK_UPLOAD_SIZE = 4 * 1024 * 1024;        // 4 MB per chunk
const CHUNK_UPLOAD_THRESHOLD = 4 * 1024 * 1024;   // files > 4 MB go chunked

/**
 * Upload a file to the legal backend using chunked transfer.
 * Calls onProgress({sent, total, chunkIndex, totalChunks}) after each chunk.
 * Returns the same response shape as the single-request /legal/upload
 * (or /legal/expediente/start when kind="expediente").
 */
async function uploadFileChunked(file, { password, sessionId, kind = "normal", onProgress }) {
  const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_UPLOAD_SIZE));

  // 1) init
  const initResp = await fetch(`${API}/legal/upload/chunk/init`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      filename: file.name,
      total_size: file.size,
      total_chunks: totalChunks,
      password,
      session_id: sessionId,
      kind,
    }),
  });
  if (!initResp.ok) {
    let detail = `Error ${initResp.status}`;
    try {
      const j = await initResp.json();
      if (j && j.detail) detail = j.detail;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  const { upload_id: uploadId } = await initResp.json();

  // 2) send each chunk sequentially (simpler, avoids overwhelming the proxy)
  for (let i = 0; i < totalChunks; i++) {
    const start = i * CHUNK_UPLOAD_SIZE;
    const end = Math.min(file.size, start + CHUNK_UPLOAD_SIZE);
    const blob = file.slice(start, end);

    const fd = new FormData();
    fd.append("upload_id", uploadId);
    fd.append("chunk_index", String(i));
    fd.append("password", password);
    fd.append("chunk", blob, `${file.name}.part${i}`);

    let lastErr = null;
    // Retry each chunk up to 2 times on transient network errors / 5xx / 520
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const resp = await fetch(`${API}/legal/upload/chunk/part`, { method: "POST", body: fd });
        if (resp.ok) {
          lastErr = null;
          break;
        }
        // 4xx other than 429 should not be retried
        if (resp.status >= 400 && resp.status < 500 && resp.status !== 429) {
          let detail = `Error ${resp.status}`;
          try {
            const j = await resp.json();
            if (j && j.detail) detail = j.detail;
          } catch {
            /* ignore */
          }
          throw new Error(detail);
        }
        lastErr = new Error(`Error ${resp.status} al subir fragmento ${i + 1}/${totalChunks}`);
      } catch (err) {
        lastErr = err;
      }
      // Backoff before retry: 500 ms, 1500 ms
      await new Promise((res) => setTimeout(res, 500 + attempt * 1000));
    }
    if (lastErr) throw lastErr;

    if (typeof onProgress === "function") {
      onProgress({
        sent: end,
        total: file.size,
        chunkIndex: i + 1,
        totalChunks,
      });
    }
  }

  // 3) finish → returns the same payload as /legal/upload or /legal/expediente/start
  const finishResp = await fetch(`${API}/legal/upload/chunk/finish`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ upload_id: uploadId, password, kind }),
  });
  if (!finishResp.ok) {
    let detail = `Error ${finishResp.status}`;
    try {
      const j = await finishResp.json();
      if (j && j.detail) detail = j.detail;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  return await finishResp.json();
}

// Generate a per-tab session id (stable while the chat is open)
const _uuid = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : "s-" + Math.random().toString(36).slice(2) + Date.now().toString(36)).replace(/-/g, "").slice(0, 24);

const MODES = [
  { id: "abogado", label: "Abogado", desc: "Respuesta clara y profesional" },
  { id: "profesor", label: "Profesor", desc: "Explicación didáctica" },
  { id: "juez", label: "Juez", desc: "Análisis formal e imparcial" },
];

const INITIAL_GREETING = {
  role: "assistant",
  content:
    "Hola, soy PreLegal.cl, tu asistente legal especializado en derecho civil y penal chileno. Cuéntame tu caso o consulta y te ayudaré con gusto.",
  sources: [],
};

// --- localStorage (lightweight boot state only: password, currentConvId, mode) ---
// The heavy data (messages, sessionDocs) lives in MongoDB via /api/legal/conversations.
const STORAGE_KEY = "legal_chat_state_v1";

const loadPersistedState = () => {
  try {
    if (typeof window === "undefined") return {};
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const clearPersistedState = () => {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
};

// Strip heavy/volatile fields from messages before persisting/sending (e.g. streaming flag)
const serializableMessages = (msgs) =>
  (msgs || []).map((m) => {
    const { streaming: _streaming, ...rest } = m;
    return rest;
  });

const persistBootState = (state) => {
  try {
    // Only persist lightweight fields — everything else is in MongoDB
    const light = {
      password: state.password || "",
      currentConvId: state.currentConvId || "",
      mode: state.mode || "abogado",
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(light));
  } catch {
    /* ignore */
  }
};

export default function LegalChatPage() {
  // Read persisted state once on first render (lazy initializers)
  const persisted = (typeof window !== "undefined") ? loadPersistedState() : {};

  // --- Auth gate ---
  const [password, setPassword] = useState(persisted.password || "");
  // Optimistically mark as authed if we had a saved password — we'll revalidate in background
  const [authed, setAuthed] = useState(!!persisted.password);
  const [authError, setAuthError] = useState("");
  const [authLoading, setAuthLoading] = useState(false);

  // --- Conversation sidebar state ---
  const [conversations, setConversations] = useState([]); // [{conv_id, title, mode, updated_at}]
  const [currentConvId, setCurrentConvId] = useState(persisted.currentConvId || "");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [convsLoading, setConvsLoading] = useState(false);

  // --- Chat state (empty until we load/create a conversation) ---
  const [mode, setMode] = useState(persisted.mode || "abogado");
  const [messages, setMessages] = useState([INITIAL_GREETING]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sessionDocs, setSessionDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // {name, sent, total, chunkIndex, totalChunks}
  const [expedienteJob, setExpedienteJob] = useState(null); // {job_id, progress, total, status, filename}
  const [reconnectAttempt, setReconnectAttempt] = useState(0); // 0 = no reconnect in progress; N = Nth invisible retry
  // Brief shine effect across the chat whenever the active conversation changes
  const [shineKey, setShineKey] = useState(0);
  // Templates modal state
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [activeTemplate, setActiveTemplate] = useState(null); // one of LEGAL_TEMPLATES
  const [templateData, setTemplateData] = useState({});       // key -> value as user fills the form
  // sessionId is now bound to the active conversation; falls back to a local uuid if no conv loaded yet
  const sessionIdRef = useRef(persisted.currentConvId || _uuid());

  // Debounced-save machinery: we auto-save the active conversation to the backend
  // a short time after every change, avoiding a hit per-keystroke.
  const saveTimerRef = useRef(null);
  const skipNextSaveRef = useRef(false); // used during conversation load to avoid re-saving just-fetched data

  const scrollRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  // Auto-grow textarea based on content
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    const max = 260;
    ta.style.height = Math.min(ta.scrollHeight, max) + "px";
  }, [input]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // ----------------------------------------------------------------------
  // Conversation management helpers
  // ----------------------------------------------------------------------
  const fetchConversations = async (pw) => {
    try {
      setConvsLoading(true);
      const resp = await axios.get(`${API}/legal/conversations`, {
        params: { password: pw || password },
      });
      setConversations(resp.data.conversations || []);
      return resp.data.conversations || [];
    } catch (err) {
      if (err.response && err.response.status === 401) {
        clearPersistedState();
        setAuthed(false);
        setPassword("");
      }
      return [];
    } finally {
      setConvsLoading(false);
    }
  };

  const createConversation = async (initialMessages, initialDocs, initialMode, pw) => {
    try {
      const resp = await axios.post(`${API}/legal/conversations`, { password: pw || password });
      const conv = resp.data;
      // Optional: seed with initial messages/docs/mode (used for migration of old localStorage)
      if ((initialMessages && initialMessages.length) || (initialDocs && initialDocs.length)) {
        await axios.put(`${API}/legal/conversations/${conv.conv_id}`, {
          password: pw || password,
          messages: serializableMessages(initialMessages || []),
          session_docs: initialDocs || [],
          mode: initialMode || "abogado",
        });
      }
      return conv;
    } catch (err) {
      setError("No se pudo crear la conversación: " + (err.message || err));
      throw err;
    }
  };

  const loadConversationById = async (convId) => {
    if (!convId) return;
    try {
      const resp = await axios.get(`${API}/legal/conversations/${convId}`, {
        params: { password },
      });
      const c = resp.data;
      // Avoid triggering the auto-save right after we just loaded from the server
      skipNextSaveRef.current = true;
      setCurrentConvId(c.conv_id);
      sessionIdRef.current = c.session_id || c.conv_id;
      setMode(c.mode || "abogado");
      setMessages(
        Array.isArray(c.messages) && c.messages.length > 0 ? c.messages : [INITIAL_GREETING]
      );
      setSessionDocs(Array.isArray(c.session_docs) ? c.session_docs : []);
      setError("");
    } catch (err) {
      if (err.response && err.response.status === 404) {
        setError("La conversación guardada ya no existe; se creará una nueva.");
        setCurrentConvId("");
      } else {
        setError("No se pudo cargar la conversación.");
      }
    }
  };

  const saveCurrentConversation = async () => {
    if (!currentConvId) return;
    try {
      await axios.put(`${API}/legal/conversations/${currentConvId}`, {
        password,
        messages: serializableMessages(messages),
        session_docs: sessionDocs,
        mode,
      });
      // Refresh sidebar metadata (title may have changed on first user msg)
      fetchConversations(password);
    } catch (err) {
      if (err.response && err.response.status === 401) {
        clearPersistedState();
        setAuthed(false);
        setPassword("");
      }
      // Silent failure otherwise — we'll try again on the next change
    }
  };

  const deleteConversationById = async (convId) => {
    if (!convId) return;
    if (!window.confirm("¿Eliminar esta conversación? Esta acción no se puede deshacer.")) return;
    try {
      await axios.delete(`${API}/legal/conversations/${convId}`, { params: { password } });
      const remaining = conversations.filter((c) => c.conv_id !== convId);
      setConversations(remaining);
      if (currentConvId === convId) {
        // Load the next most recent, or create a fresh one
        if (remaining.length > 0) {
          await loadConversationById(remaining[0].conv_id);
        } else {
          const c = await createConversation();
          setConversations([
            { conv_id: c.conv_id, title: c.title, mode: c.mode, updated_at: c.updated_at },
          ]);
          skipNextSaveRef.current = true;
          setCurrentConvId(c.conv_id);
          sessionIdRef.current = c.session_id;
          setMessages([INITIAL_GREETING]);
          setSessionDocs([]);
        }
      }
    } catch (err) {
      setError("No se pudo eliminar la conversación.");
    }
  };

  const renameConversation = async (convId, newTitle) => {
    if (!convId || !newTitle) return;
    try {
      await axios.put(`${API}/legal/conversations/${convId}`, {
        password,
        title: newTitle.trim(),
      });
      setConversations((prev) =>
        prev.map((c) => (c.conv_id === convId ? { ...c, title: newTitle.trim() } : c))
      );
    } catch (err) {
      setError("No se pudo renombrar la conversación.");
    }
  };

  const regenerateConvTitle = async (convId) => {
    try {
      const resp = await axios.post(`${API}/legal/conversations/${convId}/regenerate-title`, {
        password,
      });
      setConversations((prev) =>
        prev.map((c) => (c.conv_id === convId ? { ...c, title: resp.data.title } : c))
      );
    } catch {
      /* silent — user can still rename manually */
    }
  };

  // ----------------------------------------------------------------------
  // Persist the lightweight boot state (password, currentConvId, mode) to localStorage
  // so the next page load can skip the login prompt and auto-select the last conversation.
  // ----------------------------------------------------------------------
  useEffect(() => {
    if (!authed) return;
    persistBootState({ password, currentConvId, mode });
  }, [authed, password, currentConvId, mode]);

  // Trigger a subtle "shine" whenever the active conversation changes (while logged in).
  useEffect(() => {
    if (!authed || !currentConvId) return;
    setShineKey((k) => k + 1);
  }, [currentConvId, authed]);

  // ----------------------------------------------------------------------
  // On mount (and whenever authed flips true): revalidate password, load the
  // conversation list, and restore either the last active conversation or
  // create a new one. Also migrates any legacy heavy localStorage payload.
  // ----------------------------------------------------------------------
  useEffect(() => {
    if (!authed || !password) return;
    let cancelled = false;
    (async () => {
      // 1. Revalidate password
      try {
        await axios.post(`${API}/legal/auth`, { password });
      } catch (err) {
        if (cancelled) return;
        if (err.response && err.response.status === 401) {
          clearPersistedState();
          setAuthed(false);
          setPassword("");
          setMessages([INITIAL_GREETING]);
          setSessionDocs([]);
          setAuthError("La sesión guardada expiró, vuelve a ingresar.");
          return;
        }
      }
      if (cancelled) return;

      // 2. Fetch conversation list
      const list = await fetchConversations(password);
      if (cancelled) return;

      // 3. Decide which conversation to load
      let targetId = currentConvId;
      if (targetId && !list.find((c) => c.conv_id === targetId)) {
        // The persisted currentConvId was deleted on another device — drop it
        targetId = "";
        setCurrentConvId("");
      }

      // 4. Legacy migration: if the old localStorage had inline messages, create a
      //    new conversation with them and promote it to the active one.
      try {
        const rawLegacy = window.localStorage.getItem(STORAGE_KEY);
        const legacy = rawLegacy ? JSON.parse(rawLegacy) : null;
        const legacyMsgs = legacy && Array.isArray(legacy.messages) ? legacy.messages : null;
        const legacyDocs = legacy && Array.isArray(legacy.sessionDocs) ? legacy.sessionDocs : null;
        const hasInterestingLegacy =
          (legacyMsgs && legacyMsgs.length > 1) || (legacyDocs && legacyDocs.length > 0);
        if (!targetId && hasInterestingLegacy) {
          const conv = await createConversation(
            legacyMsgs || [],
            legacyDocs || [],
            (legacy && legacy.mode) || mode,
            password
          );
          targetId = conv.conv_id;
          setCurrentConvId(targetId);
          // Refresh the sidebar
          fetchConversations(password);
        }
      } catch {
        /* ignore legacy parse errors */
      }
      if (cancelled) return;

      // 5. Load the target conversation, or create a fresh one if the list is empty
      if (targetId) {
        await loadConversationById(targetId);
      } else if (list.length > 0) {
        await loadConversationById(list[0].conv_id);
      } else {
        const conv = await createConversation([], [], mode, password);
        if (cancelled) return;
        setConversations([
          { conv_id: conv.conv_id, title: conv.title, mode: conv.mode, updated_at: conv.updated_at },
        ]);
        skipNextSaveRef.current = true;
        setCurrentConvId(conv.conv_id);
        sessionIdRef.current = conv.session_id;
        setMessages([INITIAL_GREETING]);
        setSessionDocs([]);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  // ----------------------------------------------------------------------
  // Auto-save: debounce writes to MongoDB every 1.5s after the user stops typing/receiving.
  // ----------------------------------------------------------------------
  useEffect(() => {
    if (!authed || !currentConvId) return;
    if (skipNextSaveRef.current) {
      skipNextSaveRef.current = false;
      return;
    }
    // Don't save while an assistant reply is still streaming — the partial text would be
    // overwritten by the final version moments later. We'll save once streaming completes.
    if (loading) return;

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveCurrentConversation();
    }, 1500);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, sessionDocs, mode, currentConvId, authed, loading]);

  const handleAuth = async (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    setAuthLoading(true);
    setAuthError("");
    try {
      await axios.post(`${API}/legal/auth`, { password });
      setAuthed(true);
    } catch (err) {
      setAuthError(err.response?.data?.detail || "Contraseña inválida");
    } finally {
      setAuthLoading(false);
    }
  };

  const handleSend = async (e, overrideText) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    const text = (typeof overrideText === "string" ? overrideText : input).trim();
    if ((!text && sessionDocs.length === 0) || loading) return;

    // Build display content for user bubble. We mark which docs are newly introduced
    // this turn vs those persisting from prior turns, so the bot can acknowledge newness.
    const displayContent = text || (sessionDocs.length > 0 ? "(usa los documentos adjuntos)" : "");
    const attachmentIds = sessionDocs.map((d) => d.doc_id).filter(Boolean);
    const docsForDisplay = sessionDocs.map(({ kind, name }) => ({ kind, name }));

    const newUserMsg = { role: "user", content: displayContent, attachments: docsForDisplay };
    const nextMessages = [...messages, newUserMsg];
    // Add a placeholder assistant message that we'll fill as chunks arrive
    setMessages([...nextMessages, { role: "assistant", content: "", sources: [], streaming: true }]);
    setInput("");
    // NOTE: do NOT clear sessionDocs — they persist across turns until user removes them.
    setError("");
    setLoading(true);
    setReconnectAttempt(0);

    // Build the base history that the API expects (role+content only).
    const baseHistory = nextMessages
      .filter((m) => m.role === "user" || m.role === "assistant")
      .map(({ role, content }) => ({ role, content: typeof content === "string" ? content : "" }));
    if (baseHistory.length > 0 && baseHistory[baseHistory.length - 1].role === "user") {
      baseHistory[baseHistory.length - 1].content = text || "Por favor revisa los documentos que te adjunté.";
    }

    // Accumulators that persist across retries
    const acc = { text: "", sources: [] };
    const MAX_RETRIES = 3;
    let attempt = 0;
    let terminalError = null;

    // Helper: build the messages payload. If we already have partial text, we append
    // the partial assistant turn + a system-ish user turn asking to continue.
    const buildMessagesForAttempt = () => {
      if (!acc.text) return baseHistory;
      return [
        ...baseHistory,
        { role: "assistant", content: acc.text },
        {
          role: "user",
          content:
            "[SISTEMA] La conexión se cortó. Continúa EXACTAMENTE desde donde te quedaste, " +
            "SIN repetir nada de lo ya escrito y SIN preámbulos ni disculpas. " +
            "Reanuda la redacción directamente.",
        },
      ];
    };

    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (attempt > 0) {
          setReconnectAttempt(attempt);
          // Backoff before retry: 1s, 2s, 4s
          await new Promise((r) => setTimeout(r, Math.min(4000, 1000 * Math.pow(2, attempt - 1))));
        }

        const result = await doStreamRequest(
          {
            messages: buildMessagesForAttempt(),
            mode,
            password,
            attachment_ids: attachmentIds,
            session_id: sessionIdRef.current,
          },
          // onSources — only accept on the first attempt (subsequent retries are continuations)
          (srcs) => {
            if (attempt === 0 && srcs) {
              acc.sources = srcs;
              setMessages((prev) => {
                const copy = [...prev];
                copy[copy.length - 1] = { ...copy[copy.length - 1], sources: acc.sources };
                return copy;
              });
            }
          },
          // onDelta
          (delta) => {
            acc.text += delta;
            setMessages((prev) => {
              const copy = [...prev];
              copy[copy.length - 1] = { ...copy[copy.length - 1], content: acc.text };
              return copy;
            });
          }
        );

        if (result.status === "done") {
          break; // success
        }
        if (result.status === "auth_fail") {
          clearPersistedState();
          setAuthed(false);
          setPassword("");
          terminalError = "Sesión expirada, vuelve a ingresar.";
          break;
        }
        if (result.status === "client_error") {
          terminalError = result.error || "Error en la solicitud";
          break;
        }
        // transient_error → retry if budget remains
        attempt++;
        if (attempt > MAX_RETRIES) {
          terminalError = acc.text
            ? "La conexión falló varias veces. Lo que alcancé a redactar quedó arriba — puedes pedirme que continúe."
            : result.error || "No se pudo conectar después de varios intentos.";
          break;
        }
        // else: loop again (the next iteration will show reconnect indicator + backoff)
      }

      if (terminalError) {
        throw new Error(terminalError);
      }

      // Mark last message as done streaming
      setMessages((prev) => {
        const copy = [...prev];
        const last = copy[copy.length - 1];
        if (last && last.role === "assistant") {
          copy[copy.length - 1] = { ...last, streaming: false };
        }
        return copy;
      });
    } catch (err) {
      const msg = err.message || "Error desconocido";
      setError(msg);
      // Keep partial content if we have it (user can still read what was generated)
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last && last.role === "assistant" && !last.content) {
          return prev.slice(0, -1);
        }
        // If we have partial text, just mark it as no-longer-streaming
        if (last && last.role === "assistant" && last.streaming) {
          const copy = [...prev];
          copy[copy.length - 1] = { ...last, streaming: false };
          return copy;
        }
        return prev;
      });
    } finally {
      setLoading(false);
      setReconnectAttempt(0);
    }
  };

  // Run a single SSE streaming request. Returns a detailed result
  // describing how the stream ended (so handleSend can decide whether to retry).
  //   status: "done"           → stream ended with explicit "done" event (success)
  //           "auth_fail"      → 401 (stop, log out)
  //           "client_error"   → 4xx (except 401/429) — show error, don't retry
  //           "transient_error"→ 5xx / 429 / network / stream cut (retry-able)
  async function doStreamRequest(body, onSources, onDelta) {
    let resp;
    try {
      resp = await fetch(`${API}/legal/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
        body: JSON.stringify(body),
      });
    } catch (err) {
      return { status: "transient_error", error: err.message || "Error de red" };
    }

    if (!resp.ok) {
      if (resp.status === 401) return { status: "auth_fail" };
      if (resp.status === 429 || resp.status >= 500) {
        return { status: "transient_error", error: `Error ${resp.status}` };
      }
      let detail = `Error ${resp.status}`;
      try {
        const j = await resp.json();
        if (j && j.detail) detail = j.detail;
      } catch {
        /* ignore */
      }
      return { status: "client_error", error: detail };
    }

    const reader = resp.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let buffer = "";
    let gotDoneEvent = false;
    let serverErrorMsg = null;

    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop() || "";
        for (const raw of events) {
          const line = raw.split("\n").find((l) => l.startsWith("data:"));
          if (!line) continue;
          const payload = line.slice(5).trim();
          if (!payload) continue;
          let evt;
          try {
            evt = JSON.parse(payload);
          } catch {
            continue; // ignore malformed
          }
          if (evt.type === "sources") {
            onSources(evt.data || []);
          } else if (evt.type === "delta") {
            onDelta(evt.data || "");
          } else if (evt.type === "done") {
            gotDoneEvent = true;
          } else if (evt.type === "error") {
            serverErrorMsg = evt.data || "Error del servidor";
          }
        }
      }
    } catch (readErr) {
      // Network error while reading the stream → transient
      return {
        status: "transient_error",
        error: readErr.message || "Conexión interrumpida durante el stream",
      };
    }

    if (serverErrorMsg) {
      // Server-explicit error event: retry-able if it looks transient, otherwise fail
      // We treat them all as transient except explicit 401/400-ish strings.
      return { status: "transient_error", error: serverErrorMsg };
    }
    if (gotDoneEvent) {
      return { status: "done" };
    }
    // Stream ended without a "done" event → Cloudflare / proxy dropped the connection
    return { status: "transient_error", error: "Conexión interrumpida (sin evento 'done')" };
  }

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNewChat = async () => {
    setError("");
    setInput("");
    setExpedienteJob(null);
    try {
      const conv = await createConversation([], [], mode, password);
      setConversations((prev) => [
        { conv_id: conv.conv_id, title: conv.title, mode: conv.mode, updated_at: conv.updated_at },
        ...prev,
      ]);
      skipNextSaveRef.current = true;
      setCurrentConvId(conv.conv_id);
      sessionIdRef.current = conv.session_id;
      setMessages([INITIAL_GREETING]);
      setSessionDocs([]);
    } catch {
      /* error already surfaced by createConversation */
    }
  };

  const handleLogout = () => {
    clearPersistedState();
    setAuthed(false);
    setPassword("");
    setAuthError("");
    setMessages([INITIAL_GREETING]);
    setSessionDocs([]);
    setExpedienteJob(null);
    setInput("");
    setError("");
    setConversations([]);
    setCurrentConvId("");
    sessionIdRef.current = _uuid();
  };

  // ----------------------------------------------------------------------
  // Templates: handler that converts a filled template into a natural prompt
  // and sends it through the normal chat flow (reusing handleSend's logic).
  // This way: SSE, retries, crosscheck, auto-save and .docx generation all
  // keep working for templated requests. The chat experience is unchanged —
  // the template just saves the user from typing the boilerplate.
  // ----------------------------------------------------------------------
  const openTemplate = (tpl) => {
    setActiveTemplate(tpl);
    const blank = {};
    (tpl.fields || []).forEach((fld) => {
      blank[fld.key] = "";
    });
    setTemplateData(blank);
  };

  const submitTemplate = async () => {
    if (!activeTemplate || loading) return;
    // Validate required fields
    const missing = (activeTemplate.fields || [])
      .filter((f) => f.required && !((templateData[f.key] || "").trim()))
      .map((f) => f.label);
    if (missing.length > 0) {
      setError("Faltan campos obligatorios: " + missing.join(", "));
      return;
    }
    const prompt = activeTemplate.buildPrompt(templateData);
    // Close the modal first so the user sees the message sending
    setTemplatesOpen(false);
    const tpl = activeTemplate;
    setActiveTemplate(null);
    setTemplateData({});
    setError("");
    // Send via the normal chat flow with an explicit override text (bypasses React state lag)
    handleSend({ preventDefault: () => {} }, prompt);
    void tpl;
  };

  const handleFilePicked = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ""; // allow re-picking same file later
    if (files.length === 0) return;
    setUploading(true);
    setError("");
    try {
      for (const file of files) {
        if (sessionDocs.length >= 20) {
          setError("Máximo 20 documentos activos en la conversación.");
          break;
        }
        // Offer expediente processing if PDF is large
        const isPdf = /\.pdf$/i.test(file.name);
        const sizeMb = file.size / (1024 * 1024);
        if (isPdf && sizeMb > 25) {
          const ok = window.confirm(
            `El PDF "${file.name}" pesa ${sizeMb.toFixed(1)} MB. Los PDFs grandes (expedientes, más de 15 páginas escaneadas) es mejor procesarlos como expediente, lo que divide el análisis en lotes y genera un resumen consolidado.\n\n` +
              `¿Quieres procesarlo como EXPEDIENTE (OK) o subirlo normal (Cancelar, solo analiza las primeras páginas)?`
          );
          if (ok) {
            await startExpedienteJob(file);
            continue;
          }
        }

        // Decide strategy: single-request for small files, chunked for large
        const useChunked = file.size > CHUNK_UPLOAD_THRESHOLD;
        let data;
        if (useChunked) {
          setUploadProgress({
            name: file.name,
            sent: 0,
            total: file.size,
            chunkIndex: 0,
            totalChunks: Math.max(1, Math.ceil(file.size / CHUNK_UPLOAD_SIZE)),
          });
          try {
            data = await uploadFileChunked(file, {
              password,
              sessionId: sessionIdRef.current,
              kind: "normal",
              onProgress: (p) => setUploadProgress({ name: file.name, ...p }),
            });
          } finally {
            setUploadProgress(null);
          }
        } else {
          const fd = new FormData();
          fd.append("file", file);
          fd.append("password", password);
          fd.append("session_id", sessionIdRef.current);
          const resp = await fetch(`${API}/legal/upload`, { method: "POST", body: fd });
          if (!resp.ok) {
            let detail = `Error ${resp.status}`;
            try {
              const ct = resp.headers.get("content-type") || "";
              if (ct.includes("application/json")) {
                const j = await resp.json();
                if (j && j.detail) detail = j.detail;
              } else {
                const txt = await resp.text();
                if (txt) detail = `Error ${resp.status}: ${txt.slice(0, 200)}`;
              }
            } catch {
              /* ignore */
            }
            throw new Error(detail);
          }
          data = await resp.json();
        }

        // New format: always returns a doc_id (text or image_pages)
        if (data.kind === "image_pages") {
          if (data.truncated_from) {
            setError(
              `El PDF tenía ${data.truncated_from} páginas. Solo se indexaron las primeras ${data.pages}. ` +
                `Para analizarlo completo, súbelo como Expediente (PDFs >25 MB activan esa opción).`
            );
          }
          setSessionDocs((prev) => [
            ...prev,
            { doc_id: data.doc_id, name: data.name, kind: "image_pages", pages: data.pages },
          ]);
        } else {
          // kind === "text" or "image"
          setSessionDocs((prev) => [
            ...prev,
            {
              doc_id: data.doc_id,
              name: data.name,
              kind: data.kind,
              chunks: data.chunks,
              chars: data.chars,
              preview: data.preview,
            },
          ]);
        }
      }
    } catch (err) {
      setError(err.message || "Error al subir archivo");
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  };

  const removeSessionDoc = (idx) => {
    setSessionDocs((prev) => prev.filter((_, i) => i !== idx));
  };

  // --- Expediente (large PDF) async processing ---
  const startExpedienteJob = async (file) => {
    try {
      let data;
      if (file.size > CHUNK_UPLOAD_THRESHOLD) {
        // Chunked path (avoids Cloudflare 413 on 25+ MB PDFs)
        setUploadProgress({
          name: file.name,
          sent: 0,
          total: file.size,
          chunkIndex: 0,
          totalChunks: Math.max(1, Math.ceil(file.size / CHUNK_UPLOAD_SIZE)),
        });
        try {
          data = await uploadFileChunked(file, {
            password,
            sessionId: sessionIdRef.current,
            kind: "expediente",
            onProgress: (p) => setUploadProgress({ name: file.name, ...p }),
          });
        } finally {
          setUploadProgress(null);
        }
      } else {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("password", password);
        fd.append("session_id", sessionIdRef.current);
        const resp = await fetch(`${API}/legal/expediente/start`, { method: "POST", body: fd });
        if (!resp.ok) {
          let detail = `Error ${resp.status}`;
          try {
            const j = await resp.json();
            if (j && j.detail) detail = j.detail;
          } catch {
            /* ignore */
          }
          throw new Error(detail);
        }
        data = await resp.json();
      }
      setExpedienteJob({
        job_id: data.job_id,
        status: "processing",
        progress: 0,
        total: 0,
        filename: data.filename,
      });
      pollExpedienteJob(data.job_id);
    } catch (err) {
      setError("No se pudo iniciar el procesamiento del expediente: " + (err.message || ""));
      setUploadProgress(null);
    }
  };

  const pollExpedienteJob = async (jobId) => {
    const poll = async () => {
      try {
        const resp = await fetch(
          `${API}/legal/expediente/status/${jobId}?password=${encodeURIComponent(password)}`
        );
        if (!resp.ok) return;
        const data = await resp.json();
        setExpedienteJob((prev) => (prev ? { ...prev, ...data } : prev));
        if (data.status === "done") {
          // Inject the expediente summary as a sessionDoc using the backend-provided doc_id
          // (the backend already indexed it chunked for RAG retrieval).
          if (data.doc_id) {
            setSessionDocs((prev) => [
              ...prev,
              {
                doc_id: data.doc_id,
                name: data.doc_name || `${data.filename} (resumen)`,
                kind: "text",
                chunks: data.chunks,
              },
            ]);
          }
          // Add an assistant message announcing completion
          setMessages((prev) => [
            ...prev,
            {
              role: "assistant",
              content:
                `Listo, terminé de procesar el expediente **${data.filename}** (${data.total} páginas). ` +
                `Ya tengo el resumen consolidado indexado y disponible para nuestra conversación.\n\n` +
                `¿Qué necesitas que analice en particular?`,
              sources: [],
            },
          ]);
          setTimeout(() => setExpedienteJob(null), 4000);
          return;
        }
        if (data.status === "error") {
          setError("El procesamiento del expediente falló: " + (data.error || ""));
          setExpedienteJob(null);
          return;
        }
        setTimeout(poll, 2500);
      } catch {
        setTimeout(poll, 4000);
      }
    };
    poll();
  };

  // --- Auth gate view ---
  if (!authed) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "linear-gradient(135deg,#0f172a 0%,#1e293b 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "2rem",
          fontFamily: "system-ui, -apple-system, sans-serif",
        }}
      >
        <form
          data-testid="legal-auth-form"
          onSubmit={handleAuth}
          style={{
            background: "#1e293b",
            border: "1px solid #334155",
            borderRadius: 16,
            padding: "2.5rem",
            maxWidth: 420,
            width: "100%",
            boxShadow: "0 25px 50px -12px rgba(0,0,0,0.5)",
          }}
        >
          <h1 style={{ color: "#f8fafc", margin: 0, marginBottom: 8, fontSize: 26, letterSpacing: "-0.01em" }}>
            PreLegal.cl
          </h1>
          <p style={{ color: "#94a3b8", fontSize: 14, marginBottom: 24 }}>
            Introduce la contraseña para acceder a tu asistente legal.
          </p>
          <input
            data-testid="legal-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Contraseña"
            autoFocus
            style={{
              width: "100%",
              padding: "12px 14px",
              borderRadius: 8,
              border: "1px solid #475569",
              background: "#0f172a",
              color: "#f8fafc",
              fontSize: 15,
              outline: "none",
              boxSizing: "border-box",
            }}
          />
          {authError && (
            <p
              data-testid="legal-auth-error"
              style={{ color: "#f87171", fontSize: 13, marginTop: 10, marginBottom: 0 }}
            >
              {authError}
            </p>
          )}
          <button
            data-testid="legal-auth-submit"
            type="submit"
            disabled={authLoading}
            style={{
              width: "100%",
              marginTop: 18,
              padding: "12px",
              borderRadius: 8,
              border: "none",
              background: authLoading ? "#475569" : "#8a6a2f",
              color: "white",
              fontSize: 15,
              fontWeight: 600,
              cursor: authLoading ? "wait" : "pointer",
            }}
          >
            {authLoading ? "Verificando..." : "Entrar"}
          </button>
        </form>
      </div>
    );
  }

  // --- Chat view (ChatGPT-style, centered) ---
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundImage: "url('/legal/bg.jpg')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundColor: "#0f172a",
        display: "flex",
        justifyContent: "center",
        fontFamily: "system-ui, -apple-system, sans-serif",
      }}
    >
      {/* Subtle dark gradient overlay for readability (keeps mid transparent) */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(180deg, rgba(15,23,42,0.75) 0%, rgba(15,23,42,0.25) 18%, rgba(15,23,42,0.25) 72%, rgba(15,23,42,0.85) 100%)",
          pointerEvents: "none",
        }}
      />

      {/* Sidebar with saved cases */}
      <ConversationsSidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        conversations={conversations}
        currentConvId={currentConvId}
        convsLoading={convsLoading}
        onSelect={async (convId) => {
          if (convId === currentConvId) return;
          // Flush any pending save before switching
          if (saveTimerRef.current) {
            clearTimeout(saveTimerRef.current);
            saveTimerRef.current = null;
            await saveCurrentConversation();
          }
          await loadConversationById(convId);
        }}
        onCreate={handleNewChat}
        onDelete={deleteConversationById}
        onRename={renameConversation}
        onRegenerateTitle={regenerateConvTitle}
      />
      <div
        style={{
          position: "relative",
          width: "100%",
          maxWidth: 820,
          height: "calc(100vh - 28px)",
          margin: "14px 0",
          display: "flex",
          flexDirection: "column",
          borderRadius: 24,
          overflow: "hidden",
          boxShadow: "0 22px 55px rgba(0, 0, 0, 0.55)",
        }}
      >
        {/* Conversation-switch shine overlay */}
        {shineKey > 0 && (
          <div
            key={shineKey}
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              overflow: "hidden",
              zIndex: 3,
              borderRadius: 0,
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: 0,
                width: "45%",
                background:
                  "linear-gradient(100deg, transparent 0%, rgba(255, 232, 180, 0.18) 48%, rgba(255, 232, 180, 0.35) 50%, rgba(255, 232, 180, 0.18) 52%, transparent 100%)",
                filter: "blur(14px)",
                mixBlendMode: "screen",
                animation: "legalRoomShine 900ms ease-out both",
              }}
            />
          </div>
        )}
        {/* Header */}
        <header
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid rgba(148,163,184,0.15)",
            background: "rgba(15,23,42,0.82)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button
              type="button"
              data-testid="legal-sidebar-toggle"
              onClick={() => setSidebarOpen((v) => !v)}
              aria-label={sidebarOpen ? "Ocultar casos" : "Mostrar casos"}
              title={sidebarOpen ? "Ocultar casos" : "Mostrar casos guardados"}
              style={{
                width: 34,
                height: 34,
                borderRadius: 8,
                border: "1px solid #334155",
                background: "#0f172a",
                color: "#cbd5e1",
                cursor: "pointer",
                fontSize: 16,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 0,
                flexShrink: 0,
              }}
            >
              {sidebarOpen ? "‹" : "☰"}
            </button>
            <div>
              <h1
                data-testid="legal-page-title"
                style={{
                  color: "#f8fafc",
                  margin: 0,
                  fontSize: 18,
                  fontWeight: 600,
                  letterSpacing: "-0.01em",
                }}
              >
                PreLegal.cl
              </h1>
              <p style={{ color: "#64748b", fontSize: 12, margin: "2px 0 0" }}>
                Tu sistema de asistencia legal inteligente
              </p>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button
              data-testid="legal-new-chat"
              onClick={handleNewChat}
              title="Nueva conversación"
              style={{
                padding: "8px 12px",
                borderRadius: 8,
                border: "1px solid #334155",
                background: "#0f172a",
                color: "#cbd5e1",
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              Nuevo chat
            </button>
            <button
              data-testid="legal-templates-open"
              onClick={() => setTemplatesOpen(true)}
              title="Generar un escrito desde plantilla (ahorra tiempo en casos típicos)"
              style={{
                padding: "8px 12px",
                borderRadius: 8,
                border: "1px solid rgba(215, 180, 120, 0.35)",
                background: "rgba(80, 55, 25, 0.45)",
                color: "#f3e6cc",
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              📝 Plantillas
            </button>
            <button
              data-testid="legal-logout"
              onClick={handleLogout}
              title="Cerrar sesión y borrar conversación guardada"
              style={{
                padding: "8px 12px",
                borderRadius: 8,
                border: "1px solid #334155",
                background: "#0f172a",
                color: "#94a3b8",
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              Cerrar sesión
            </button>
          </div>
        </header>

        {/* Messages area */}
        <div
          ref={scrollRef}
          data-testid="legal-messages-area"
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "24px 20px",
            display: "flex",
            flexDirection: "column",
            gap: 18,
          }}
        >
          {messages.map((m, idx) => (
            <MessageBubble key={idx} msg={m} password={password} />
          ))}
          {(
            (loading &&
              messages[messages.length - 1]?.role === "assistant" &&
              !messages[messages.length - 1]?.content) ||
            uploading ||
            !!uploadProgress ||
            (expedienteJob && expedienteJob.status !== "done")
          ) && (
            <div
              data-testid="legal-typing"
              style={{
                alignSelf: "flex-start",
                background: "rgba(255, 248, 235, 0.32)",
                border: "1px solid rgba(255, 255, 255, 0.38)",
                backdropFilter: "blur(18px) saturate(140%)",
                WebkitBackdropFilter: "blur(18px) saturate(140%)",
                padding: "12px 16px",
                borderRadius: 20,
                borderBottomLeftRadius: 6,
                color: "#3b2a15",
                fontSize: 13,
                display: "flex",
                alignItems: "center",
                gap: 8,
                boxShadow: "0 6px 22px rgba(20, 12, 0, 0.28)",
                animation: "legalBubbleIn 150ms ease-out both",
              }}
            >
              <div style={{ display: "flex", gap: 4 }}>
                <Dot />
                <Dot delay="0.2s" />
                <Dot delay="0.4s" />
              </div>
              {(uploading || uploadProgress) && (
                <span style={{ opacity: 0.8 }}>
                  Subiendo {uploadProgress?.name ? `“${uploadProgress.name}”` : "archivo"}…
                </span>
              )}
              {!uploading && !uploadProgress && expedienteJob && expedienteJob.status !== "done" && (
                <span style={{ opacity: 0.8 }}>
                  Procesando expediente{expedienteJob.filename ? ` “${expedienteJob.filename}”` : ""}…
                </span>
              )}
            </div>
          )}
          {reconnectAttempt > 0 && (
            <div
              data-testid="legal-reconnecting"
              style={{
                alignSelf: "flex-start",
                background: "rgba(250, 204, 21, 0.12)",
                border: "1px solid rgba(250, 204, 21, 0.35)",
                color: "#fde68a",
                padding: "6px 12px",
                borderRadius: 999,
                fontSize: 12,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 999,
                  background: "#fbbf24",
                  display: "inline-block",
                  animation: "legalBlink 1.2s infinite both",
                }}
              />
              Reconectando… (intento {reconnectAttempt}/3)
            </div>
          )}
          {error && (
            <div
              data-testid="legal-error"
              style={{
                background: "#7f1d1d",
                border: "1px solid #b91c1c",
                color: "#fecaca",
                padding: 12,
                borderRadius: 10,
                fontSize: 13,
                alignSelf: "center",
              }}
            >
              {error}
            </div>
          )}
        </div>

        {/* Input area */}
        <form
          onSubmit={handleSend}
          data-testid="legal-ask-form"
          style={{
            padding: "12px 20px 20px",
            borderTop: "1px solid rgba(148,163,184,0.15)",
            background: "rgba(15,23,42,0.82)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
          }}
        >
          {/* Active session documents (persist across turns) */}
          {sessionDocs.length > 0 && (
            <div
              data-testid="legal-session-docs"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 4,
                marginBottom: 8,
                padding: "6px 8px",
                background: "rgba(176, 139, 62, 0.14)",
                border: "1px solid rgba(215, 180, 120, 0.35)",
                borderRadius: 8,
              }}
            >
              <span style={{ fontSize: 10, color: "#e8dcc2", fontWeight: 600, letterSpacing: "0.04em" }}>
                📎 DOCUMENTOS ACTIVOS EN LA CONVERSACIÓN ({sessionDocs.length})
              </span>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {sessionDocs.map((a, i) => (
                  <div
                    key={i}
                    style={{
                      background: "rgba(80, 55, 25, 0.55)",
                      border: "1px solid rgba(215, 180, 120, 0.4)",
                      backdropFilter: "blur(8px)",
                      WebkitBackdropFilter: "blur(8px)",
                      borderRadius: 8,
                      padding: "6px 10px",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      fontSize: 12,
                      color: "#f3e6cc",
                      maxWidth: 260,
                    }}
                  >
                    <span style={{ fontSize: 14 }}>{a.kind === "image" || a.kind === "image_pages" ? "🖼️" : "📄"}</span>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {a.name}
                      {a.chunks ? (
                        <span style={{ opacity: 0.6, marginLeft: 4, fontSize: 10 }}>· {a.chunks} frag</span>
                      ) : a.pages ? (
                        <span style={{ opacity: 0.6, marginLeft: 4, fontSize: 10 }}>· {a.pages} pág</span>
                      ) : null}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeSessionDoc(i)}
                      aria-label="Quitar documento de la conversación"
                      title="Quitar de la conversación"
                      style={{
                        background: "transparent",
                        border: "none",
                        color: "#e8dcc2",
                        cursor: "pointer",
                        fontSize: 14,
                        lineHeight: 1,
                        padding: 0,
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Chunked upload progress (runs before expediente job begins, or during normal large upload) */}
          {uploadProgress && (
            <div
              data-testid="legal-upload-progress"
              style={{
                marginBottom: 8,
                padding: "10px 14px",
                background: "rgba(80, 55, 25, 0.55)",
                border: "1px solid rgba(215, 180, 120, 0.4)",
                backdropFilter: "blur(10px)",
                WebkitBackdropFilter: "blur(10px)",
                borderRadius: 14,
                fontSize: 12,
                color: "#f3e6cc",
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                📤 Subiendo por fragmentos: {uploadProgress.name}
              </div>
              <div>
                Fragmento {uploadProgress.chunkIndex || 0} / {uploadProgress.totalChunks} ·{" "}
                {(uploadProgress.sent / (1024 * 1024)).toFixed(1)} MB de{" "}
                {(uploadProgress.total / (1024 * 1024)).toFixed(1)} MB
              </div>
              <div
                style={{
                  marginTop: 6,
                  height: 6,
                  background: "rgba(215, 180, 120, 0.25)",
                  borderRadius: 999,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    background: "#d4a24a",
                    width: `${Math.min(
                      100,
                      Math.round((uploadProgress.sent / Math.max(1, uploadProgress.total)) * 100)
                    )}%`,
                    transition: "width 0.25s ease",
                    borderRadius: 999,
                  }}
                />
              </div>
              <div style={{ marginTop: 4, fontSize: 11, opacity: 0.75 }}>
                Dividimos el archivo en partes de 4 MB para evitar los límites de Cloudflare.
              </div>
            </div>
          )}

          {/* Expediente (large PDF) progress */}
          {expedienteJob && expedienteJob.status !== "done" && (
            <div
              data-testid="legal-expediente-progress"
              style={{
                marginBottom: 8,
                padding: "10px 14px",
                background: "rgba(110, 75, 30, 0.55)",
                border: "1px solid rgba(215, 180, 120, 0.45)",
                backdropFilter: "blur(10px)",
                WebkitBackdropFilter: "blur(10px)",
                borderRadius: 14,
                fontSize: 12,
                color: "#fff3d6",
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                📋 Procesando expediente: {expedienteJob.filename}
              </div>
              <div>
                {expedienteJob.status === "consolidating"
                  ? "Consolidando el resumen final..."
                  : `Progreso: ${expedienteJob.progress || 0}${expedienteJob.total ? ` / ${expedienteJob.total}` : ""} páginas analizadas`}
              </div>
              {expedienteJob.total > 0 && (
                <div
                  style={{
                    marginTop: 6,
                    height: 6,
                    background: "rgba(255, 230, 180, 0.22)",
                    borderRadius: 999,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      background: "#e8c278",
                      width: `${Math.min(100, Math.round((expedienteJob.progress / expedienteJob.total) * 100))}%`,
                      transition: "width 0.4s ease",
                      borderRadius: 999,
                    }}
                  />
                </div>
              )}
              <div style={{ marginTop: 4, fontSize: 11, opacity: 0.8 }}>
                Esto puede tomar varios minutos. Puedes seguir chateando mientras tanto.
              </div>
            </div>
          )}

          <div
            style={{
              display: "flex",
              gap: 10,
              background: "#efe1c2",
              border: "1px solid #c9b896",
              borderRadius: 14,
              padding: 10,
              alignItems: "flex-end",
              boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,.txt,.xlsx,image/png,image/jpeg,image/webp,image/gif"
              multiple
              onChange={handleFilePicked}
              style={{ display: "none" }}
              data-testid="legal-file-input"
            />
            <button
              type="button"
              data-testid="legal-attach-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || loading}
              title="Adjuntar documento o imagen"
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                border: "1px solid #c9b896",
                background: uploading ? "#d8c79f" : "#f7eccf",
                color: "#6b4e1f",
                cursor: uploading || loading ? "wait" : "pointer",
                fontSize: 18,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 0,
                flexShrink: 0,
              }}
            >
              {uploading ? "…" : "📎"}
            </button>
            <textarea
              ref={textareaRef}
              data-testid="legal-question-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              maxLength={1500}
              placeholder="Escribe tu consulta legal... (Enter para enviar, Shift+Enter para salto de línea)"
              style={{
                flex: 1,
                padding: "8px 10px",
                border: "none",
                background: "transparent",
                color: "#2c1f0a",
                fontSize: 15,
                outline: "none",
                resize: "none",
                minHeight: 24,
                maxHeight: 260,
                overflow: "auto",
                fontFamily: "inherit",
                lineHeight: 1.5,
              }}
            />
            <button
              data-testid="legal-ask-submit"
              type="submit"
              disabled={loading || uploading || (!input.trim() && sessionDocs.length === 0)}
              style={{
                padding: "8px 18px",
                borderRadius: 10,
                border: "none",
                background:
                  loading || uploading || (!input.trim() && sessionDocs.length === 0)
                    ? "#b4a57f"
                    : "#8a6a2f",
                color: "#fff8eb",
                fontWeight: 600,
                fontSize: 14,
                cursor:
                  loading || uploading || (!input.trim() && sessionDocs.length === 0)
                    ? "not-allowed"
                    : "pointer",
                height: 38,
                flexShrink: 0,
              }}
            >
              Enviar
            </button>
          </div>
          <p
            style={{
              color: "#475569",
              fontSize: 11,
              margin: "8px 0 0",
              textAlign: "center",
            }}
          >
            PreLegal.cl puede cometer errores. Verifica información crítica. · Formatos: PDF, Word, TXT, Excel, imágenes (máx 15 MB)
          </p>
        </form>
      </div>

      {/* Legal footer — floating bottom-right links */}
      <LegalFooterLinks />

      {/* Templates modal */}
      {templatesOpen && (
        <TemplatesModal
          activeTemplate={activeTemplate}
          templateData={templateData}
          onChangeField={(key, val) => setTemplateData((prev) => ({ ...prev, [key]: val }))}
          onPickTemplate={openTemplate}
          onBack={() => setActiveTemplate(null)}
          onClose={() => {
            setTemplatesOpen(false);
            setActiveTemplate(null);
            setTemplateData({});
          }}
          onSubmit={submitTemplate}
          loading={loading}
        />
      )}
    </div>
  );
}

// Floating footer with links to Terms / Privacy / Legal notice
function TemplatesModal({
  activeTemplate,
  templateData,
  onChangeField,
  onPickTemplate,
  onBack,
  onClose,
  onSubmit,
  loading,
}) {
  // Group templates by category for the picker
  const grouped = LEGAL_TEMPLATES.reduce((acc, t) => {
    const cat = t.category || "Otros";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(t);
    return acc;
  }, {});

  return (
    <div
      data-testid="legal-templates-modal"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(8, 5, 2, 0.72)",
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
        zIndex: 50,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "rgba(30, 20, 8, 0.96)",
          border: "1px solid rgba(215, 180, 120, 0.3)",
          borderRadius: 18,
          width: "100%",
          maxWidth: 640,
          maxHeight: "90vh",
          overflowY: "auto",
          color: "#f3e6cc",
          boxShadow: "0 22px 55px rgba(0,0,0,0.55)",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "18px 22px",
            borderBottom: "1px solid rgba(215, 180, 120, 0.2)",
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          {activeTemplate && (
            <button
              onClick={onBack}
              title="Volver al catálogo"
              style={{
                width: 30,
                height: 30,
                borderRadius: 999,
                border: "1px solid rgba(215, 180, 120, 0.3)",
                background: "transparent",
                color: "#f3e6cc",
                cursor: "pointer",
                fontSize: 14,
                padding: 0,
              }}
            >
              ←
            </button>
          )}
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, letterSpacing: "0.2em", color: "rgba(215, 180, 120, 0.8)", fontWeight: 600 }}>
              {activeTemplate ? "COMPLETA LOS DATOS" : "PLANTILLAS DE ESCRITOS"}
            </div>
            <div style={{ fontSize: 17, fontWeight: 600, color: "#fff8eb", marginTop: 2 }}>
              {activeTemplate ? `${activeTemplate.icon} ${activeTemplate.name}` : "Elige un tipo de escrito"}
            </div>
          </div>
          <button
            data-testid="legal-templates-close"
            onClick={onClose}
            title="Cerrar"
            style={{
              width: 30,
              height: 30,
              borderRadius: 999,
              border: "1px solid rgba(215, 180, 120, 0.3)",
              background: "transparent",
              color: "#f3e6cc",
              cursor: "pointer",
              fontSize: 16,
              padding: 0,
            }}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: "18px 22px" }}>
          {!activeTemplate ? (
            // ---- Catalog ----
            <>
              <p style={{ fontSize: 13, color: "rgba(243, 230, 204, 0.8)", marginTop: 0, marginBottom: 18 }}>
                Selecciona una plantilla para ahorrar tiempo. El borrador se generará en tu
                conversación actual y podrás seguir conversando con el asistente normalmente
                para ajustes.
              </p>
              {Object.entries(grouped).map(([cat, items]) => (
                <div key={cat} style={{ marginBottom: 22 }}>
                  <div
                    style={{
                      fontSize: 11,
                      letterSpacing: "0.14em",
                      color: "rgba(215, 180, 120, 0.7)",
                      fontWeight: 600,
                      marginBottom: 8,
                    }}
                  >
                    {cat.toUpperCase()}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {items.map((t) => (
                      <button
                        key={t.id}
                        data-testid={`legal-template-${t.id}`}
                        onClick={() => onPickTemplate(t)}
                        style={{
                          textAlign: "left",
                          padding: "12px 14px",
                          borderRadius: 12,
                          border: "1px solid rgba(215, 180, 120, 0.2)",
                          background: "rgba(80, 55, 25, 0.35)",
                          color: "#f3e6cc",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          transition: "background 0.15s, border-color 0.15s",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "rgba(110, 75, 30, 0.55)";
                          e.currentTarget.style.borderColor = "rgba(215, 180, 120, 0.45)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "rgba(80, 55, 25, 0.35)";
                          e.currentTarget.style.borderColor = "rgba(215, 180, 120, 0.2)";
                        }}
                      >
                        <span style={{ fontSize: 22 }}>{t.icon}</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: "#fff8eb" }}>{t.name}</div>
                          <div style={{ fontSize: 12, color: "rgba(243, 230, 204, 0.65)", marginTop: 2 }}>
                            {t.description}
                          </div>
                        </div>
                        <span style={{ color: "rgba(215, 180, 120, 0.5)", fontSize: 16 }}>›</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </>
          ) : (
            // ---- Form ----
            <>
              <p style={{ fontSize: 13, color: "rgba(243, 230, 204, 0.75)", marginTop: 0, marginBottom: 16 }}>
                {activeTemplate.description} · Los campos con <span style={{ color: "#ffd28a" }}>*</span> son obligatorios.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {activeTemplate.fields.map((fld) => (
                  <div key={fld.key}>
                    <label
                      style={{
                        display: "block",
                        fontSize: 12,
                        color: "rgba(243, 230, 204, 0.85)",
                        marginBottom: 5,
                        fontWeight: 500,
                      }}
                    >
                      {fld.label}
                      {fld.required && <span style={{ color: "#ffd28a", marginLeft: 4 }}>*</span>}
                    </label>
                    {fld.type === "textarea" ? (
                      <textarea
                        data-testid={`legal-template-field-${fld.key}`}
                        value={templateData[fld.key] || ""}
                        onChange={(e) => onChangeField(fld.key, e.target.value)}
                        placeholder={fld.placeholder || ""}
                        rows={4}
                        style={inputStyle}
                      />
                    ) : (
                      <input
                        data-testid={`legal-template-field-${fld.key}`}
                        type={fld.type === "date" ? "text" : "text"}
                        value={templateData[fld.key] || ""}
                        onChange={(e) => onChangeField(fld.key, e.target.value)}
                        placeholder={fld.placeholder || ""}
                        style={inputStyle}
                      />
                    )}
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 18, display: "flex", justifyContent: "flex-end", gap: 10 }}>
                <button
                  onClick={onBack}
                  style={{
                    padding: "10px 18px",
                    borderRadius: 10,
                    border: "1px solid rgba(215, 180, 120, 0.3)",
                    background: "transparent",
                    color: "#f3e6cc",
                    fontSize: 13,
                    cursor: "pointer",
                  }}
                >
                  Volver
                </button>
                <button
                  data-testid="legal-template-submit"
                  onClick={onSubmit}
                  disabled={loading}
                  style={{
                    padding: "10px 22px",
                    borderRadius: 10,
                    border: "1px solid #8a6a2f",
                    background: loading ? "#b4a57f" : "#8a6a2f",
                    color: "#fff8eb",
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: loading ? "default" : "pointer",
                  }}
                >
                  {loading ? "Generando…" : "Generar escrito"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const inputStyle = {
  width: "100%",
  padding: "9px 12px",
  borderRadius: 10,
  border: "1px solid rgba(215, 180, 120, 0.3)",
  background: "rgba(60, 40, 18, 0.55)",
  color: "#fff8eb",
  fontSize: 13,
  fontFamily: "inherit",
  outline: "none",
  boxSizing: "border-box",
  resize: "vertical",
};

function LegalFooterLinks() {
  const linkStyle = {
    color: "rgba(243, 230, 204, 0.82)",
    textDecoration: "none",
    fontSize: 11,
    letterSpacing: "0.02em",
    transition: "color 0.15s",
  };
  return (
    <div
      data-testid="legal-footer-links"
      style={{
        position: "fixed",
        right: 16,
        bottom: 10,
        zIndex: 5,
        display: "flex",
        gap: 10,
        alignItems: "center",
        padding: "6px 12px",
        background: "rgba(30, 20, 8, 0.55)",
        backdropFilter: "blur(12px) saturate(140%)",
        WebkitBackdropFilter: "blur(12px) saturate(140%)",
        border: "1px solid rgba(215, 180, 120, 0.3)",
        borderRadius: 999,
        boxShadow: "0 6px 22px rgba(20, 12, 0, 0.35)",
      }}
    >
      <Link
        data-testid="legal-footer-terms"
        to="/prelegal/terminos"
        style={linkStyle}
        onMouseEnter={(e) => (e.currentTarget.style.color = "#fff8eb")}
        onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(243, 230, 204, 0.82)")}
      >
        Términos y Condiciones
      </Link>
      <span style={{ color: "rgba(215, 180, 120, 0.3)", fontSize: 10 }}>·</span>
      <Link
        data-testid="legal-footer-privacy"
        to="/prelegal/privacidad"
        style={linkStyle}
        onMouseEnter={(e) => (e.currentTarget.style.color = "#fff8eb")}
        onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(243, 230, 204, 0.82)")}
      >
        Política de Privacidad
      </Link>
      <span style={{ color: "rgba(215, 180, 120, 0.3)", fontSize: 10 }}>·</span>
      <Link
        data-testid="legal-footer-notice"
        to="/prelegal/aviso-legal"
        style={linkStyle}
        onMouseEnter={(e) => (e.currentTarget.style.color = "#fff8eb")}
        onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(243, 230, 204, 0.82)")}
      >
        Aviso Legal
      </Link>
    </div>
  );
}

function MessageBubble({ msg, password }) {
  const isUser = msg.role === "user";

  // Detect a legal document wrapped in markers to offer a Word download
  const DOC_START = "--- INICIO DOCUMENTO ---";
  const DOC_END = "--- FIN DOCUMENTO ---";
  const docMatch = !isUser && typeof msg.content === "string" && msg.content.includes(DOC_START) && msg.content.includes(DOC_END);
  let docContent = "";
  if (docMatch) {
    const s = msg.content.indexOf(DOC_START) + DOC_START.length;
    const e = msg.content.indexOf(DOC_END);
    docContent = msg.content.substring(s, e).trim();
  }

  const downloadDocx = async () => {
    if (!docContent) return;
    try {
      // Guess a filename from the first EN LO PRINCIPAL line
      let name = "documento-legal";
      const m = docContent.match(/EN LO PRINCIPAL[:\s]+([^\n.]+)/i);
      if (m) {
        name = m[1]
          .trim()
          .toLowerCase()
          .replace(/\*+/g, "")
          .replace(/[^a-z0-9áéíóúñ\s-]/gi, "")
          .trim()
          .replace(/\s+/g, "-")
          .slice(0, 60) || "documento-legal";
      }
      const resp = await fetch(`${API}/legal/generate-docx`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content: docContent, filename: name, password }),
      });
      if (!resp.ok) {
        let detail = `Error ${resp.status}`;
        try {
          const ct = resp.headers.get("content-type") || "";
          if (ct.includes("application/json")) {
            const j = await resp.json();
            if (j && j.detail) detail = j.detail;
          }
        } catch {
          /* ignore */
        }
        alert(detail);
        return;
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${name}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      alert("No se pudo descargar: " + (err.message || "error"));
    }
  };

  return (
    <div
      data-testid={`legal-msg-${msg.role}`}
      style={{
        alignSelf: isUser ? "flex-end" : "flex-start",
        maxWidth: "85%",
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <div
        style={{
          // Warm glassmorphism that harmonises with the beige/gold bg
          background: isUser
            ? "rgba(150, 100, 50, 0.22)"        // translucent bronze for the user (lighter)
            : "rgba(255, 248, 235, 0.32)",      // translucent cream for the assistant (lighter)
          border: isUser
            ? "1px solid rgba(255, 220, 170, 0.28)"
            : "1px solid rgba(255, 255, 255, 0.38)",
          backdropFilter: "blur(18px) saturate(140%)",
          WebkitBackdropFilter: "blur(18px) saturate(140%)",
          color: isUser ? "#fff8eb" : "#1a130a",
          textShadow: isUser ? "0 1px 3px rgba(0,0,0,0.5)" : "0 1px 1px rgba(255,255,255,0.4)",
          padding: "12px 16px",
          borderRadius: 20,
          borderBottomRightRadius: isUser ? 6 : 20,
          borderBottomLeftRadius: isUser ? 20 : 6,
          fontSize: 15,
          lineHeight: 1.6,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          boxShadow: "0 6px 22px rgba(20, 12, 0, 0.28)",
          animation: "legalBubbleIn 150ms ease-out both",
        }}
      >
        {msg.content}
      </div>
      {docMatch && !msg.streaming && (
        <button
          type="button"
          data-testid="legal-docx-download"
          onClick={downloadDocx}
          style={{
            alignSelf: "flex-start",
            background: "#2563eb",
            border: "none",
            color: "white",
            padding: "8px 14px",
            borderRadius: 10,
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          ⬇️ Descargar como Word (.docx)
        </button>
      )}
      {isUser && msg.attachments && msg.attachments.length > 0 && (
        <div
          data-testid="legal-msg-attachments"
          style={{ display: "flex", flexWrap: "wrap", gap: 6, alignSelf: "flex-end" }}
        >
          {msg.attachments.map((a, i) => (
            <div
              key={i}
              style={{
                background: "rgba(80, 55, 25, 0.55)",
                border: "1px solid rgba(215, 180, 120, 0.4)",
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
                borderRadius: 8,
                padding: "4px 10px",
                fontSize: 11,
                color: "#f3e6cc",
                display: "flex",
                alignItems: "center",
                gap: 6,
                maxWidth: 220,
              }}
            >
              <span>{a.kind === "image" ? "🖼️" : "📄"}</span>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {a.name}
              </span>
            </div>
          ))}
        </div>
      )}
      {!isUser && msg.sources && msg.sources.length > 0 && (
        <details
          data-testid="legal-sources"
          style={{
            background: "#0f172a",
            border: "1px solid #1e293b",
            borderRadius: 10,
            padding: "8px 12px",
          }}
        >
          <summary
            style={{
              cursor: "pointer",
              color: "#94a3b8",
              fontSize: 12,
              fontWeight: 500,
              listStyle: "none",
            }}
          >
            📖 Fuentes consultadas ({msg.sources.length})
          </summary>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
            {msg.sources.map((s) => (
              <div
                key={s.n}
                data-testid={`legal-source-${s.n}`}
                style={{
                  background: "#1e293b",
                  border: "1px solid #334155",
                  borderRadius: 8,
                  padding: "8px 10px",
                }}
              >
                <div style={{ color: "#cbd5e1", fontSize: 12, fontWeight: 600 }}>
                  [Fuente {s.n}] {s.codigo} — {s.source}{" "}
                  <span style={{ color: "#64748b", fontWeight: 400 }}>
                    ({(s.score * 100).toFixed(1)}%)
                  </span>
                </div>
                <p
                  style={{
                    color: "#94a3b8",
                    fontSize: 12,
                    lineHeight: 1.5,
                    margin: "6px 0 0",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {s.excerpt}
                </p>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function Dot({ delay = "0s" }) {
  return (
    <span
      style={{
        width: 7,
        height: 7,
        borderRadius: 999,
        background: "#6b4e1f",
        display: "inline-block",
        animation: "legalBlink 1.4s infinite both",
        animationDelay: delay,
      }}
    />
  );
}

// Inject keyframes once
if (typeof document !== "undefined" && !document.getElementById("legal-chat-styles")) {
  const style = document.createElement("style");
  style.id = "legal-chat-styles";
  style.innerHTML = `
    @keyframes legalBlink{0%,80%,100%{opacity:0.2}40%{opacity:1}}
    @keyframes legalBubbleIn{
      from{opacity:0;transform:translateY(8px) scale(0.985);}
      to{opacity:1;transform:translateY(0) scale(1);}
    }
    @keyframes legalRoomShine{
      0%{opacity:0;transform:translateX(-60%);}
      35%{opacity:1;}
      100%{opacity:0;transform:translateX(160%);}
    }
  `;
  document.head.appendChild(style);
}

// Sidebar listing saved cases (conversations)
function ConversationsSidebar({
  open,
  onClose,
  conversations,
  currentConvId,
  convsLoading,
  onSelect,
  onCreate,
  onDelete,
  onRename,
  onRegenerateTitle,
}) {
  const SIDEBAR_WIDTH = 280;
  return (
    <aside
      data-testid="legal-sidebar"
      aria-hidden={!open}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        bottom: 0,
        width: SIDEBAR_WIDTH,
        transform: open ? "translateX(0)" : `translateX(-${SIDEBAR_WIDTH}px)`,
        transition: "transform 0.25s ease",
        background: "rgba(10, 15, 28, 0.96)",
        borderRight: "1px solid rgba(148,163,184,0.15)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        zIndex: 20,
        display: "flex",
        flexDirection: "column",
        color: "#e2e8f0",
      }}
    >
      <div
        style={{
          padding: "16px 14px 12px",
          borderBottom: "1px solid rgba(148,163,184,0.12)",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <strong style={{ fontSize: 13, letterSpacing: "0.08em", color: "#94a3b8" }}>
          MIS CASOS
        </strong>
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar sidebar"
          title="Ocultar"
          style={{
            marginLeft: "auto",
            width: 26,
            height: 26,
            borderRadius: 6,
            border: "1px solid #334155",
            background: "#0f172a",
            color: "#cbd5e1",
            fontSize: 14,
            cursor: "pointer",
            padding: 0,
          }}
        >
          ‹
        </button>
      </div>

      <button
        type="button"
        data-testid="legal-sidebar-new"
        onClick={onCreate}
        style={{
          margin: "10px 12px",
          padding: "10px 12px",
          borderRadius: 10,
          border: "1px solid #8a6a2f",
          background: "#8a6a2f",
          color: "#fff8eb",
          fontSize: 13,
          fontWeight: 600,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
        }}
      >
        ＋ Nueva conversación
      </button>

      <div
        data-testid="legal-sidebar-list"
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "4px 8px 16px",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {convsLoading && conversations.length === 0 && (
          <div style={{ padding: 14, fontSize: 12, color: "#64748b" }}>Cargando casos…</div>
        )}
        {!convsLoading && conversations.length === 0 && (
          <div style={{ padding: 14, fontSize: 12, color: "#64748b" }}>
            Aún no tienes casos guardados. Tu conversación actual aparecerá aquí apenas envíes un mensaje.
          </div>
        )}
        {conversations.map((c) => (
          <ConversationItem
            key={c.conv_id}
            conv={c}
            active={c.conv_id === currentConvId}
            onSelect={() => onSelect(c.conv_id)}
            onDelete={() => onDelete(c.conv_id)}
            onRename={(newTitle) => onRename(c.conv_id, newTitle)}
            onRegenerateTitle={() => onRegenerateTitle(c.conv_id)}
          />
        ))}
      </div>
    </aside>
  );
}

function ConversationItem({ conv, active, onSelect, onDelete, onRename, onRegenerateTitle }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [tempTitle, setTempTitle] = useState(conv.title || "");

  const commitRename = () => {
    const t = tempTitle.trim();
    if (t && t !== conv.title) onRename(t);
    setRenaming(false);
  };

  return (
    <div
      data-testid={`legal-conv-item-${conv.conv_id}`}
      data-active={active ? "true" : "false"}
      style={{
        position: "relative",
        padding: "9px 10px",
        borderRadius: 8,
        background: active ? "rgba(169, 127, 63, 0.26)" : "transparent",
        border: active ? "1px solid rgba(215, 180, 120, 0.45)" : "1px solid transparent",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: 8,
      }}
      onMouseLeave={() => setMenuOpen(false)}
      onClick={renaming ? undefined : onSelect}
    >
      {renaming ? (
        <input
          autoFocus
          value={tempTitle}
          onChange={(e) => setTempTitle(e.target.value)}
          onBlur={commitRename}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commitRename();
            }
            if (e.key === "Escape") {
              setTempTitle(conv.title || "");
              setRenaming(false);
            }
          }}
          data-testid={`legal-conv-rename-${conv.conv_id}`}
          style={{
            flex: 1,
            padding: "4px 6px",
            background: "#0f172a",
            border: "1px solid #475569",
            borderRadius: 6,
            color: "#e2e8f0",
            fontSize: 13,
            outline: "none",
          }}
        />
      ) : (
        <span
          style={{
            flex: 1,
            fontSize: 13,
            color: active ? "#e2e8f0" : "#cbd5e1",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={conv.title}
        >
          {conv.title || "Sin título"}
        </span>
      )}

      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setMenuOpen((v) => !v);
        }}
        aria-label="Opciones"
        data-testid={`legal-conv-menu-${conv.conv_id}`}
        style={{
          width: 22,
          height: 22,
          borderRadius: 6,
          border: "none",
          background: "transparent",
          color: "#94a3b8",
          cursor: "pointer",
          fontSize: 16,
          padding: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        ⋮
      </button>

      {menuOpen && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "absolute",
            top: "100%",
            right: 4,
            marginTop: 4,
            minWidth: 160,
            background: "#1e293b",
            border: "1px solid #334155",
            borderRadius: 8,
            boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
            zIndex: 30,
            overflow: "hidden",
          }}
        >
          {[
            {
              label: "Renombrar",
              testid: `legal-conv-action-rename-${conv.conv_id}`,
              fn: () => {
                setRenaming(true);
                setMenuOpen(false);
              },
            },
            {
              label: "Título automático",
              testid: `legal-conv-action-autotitle-${conv.conv_id}`,
              fn: () => {
                onRegenerateTitle();
                setMenuOpen(false);
              },
            },
            {
              label: "Eliminar",
              testid: `legal-conv-action-delete-${conv.conv_id}`,
              fn: () => {
                onDelete();
                setMenuOpen(false);
              },
              danger: true,
            },
          ].map((opt) => (
            <button
              key={opt.label}
              type="button"
              data-testid={opt.testid}
              onClick={opt.fn}
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "none",
                background: "transparent",
                color: opt.danger ? "#f87171" : "#e2e8f0",
                fontSize: 12,
                textAlign: "left",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#334155")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
